/** Base path of the API; Caddy (and the Vite dev proxy) forward it to arqam-api. */
export const API_BASE = '/api'

const PROBLEM_CONTENT_TYPE = 'application/problem+json'

/** An RFC 9457 problem returned by the API, or a synthetic one for non-problem failures. */
export class ApiProblem extends Error {
  readonly status: number
  readonly type: string
  readonly title: string
  readonly requestId: string | undefined
  readonly body: Record<string, unknown>

  constructor(status: number, body: Record<string, unknown>) {
    const detail =
      typeof body.detail === 'string' ? body.detail : `Request failed (${String(status)})`
    super(detail)
    this.name = 'ApiProblem'
    this.status = status
    this.type = typeof body.type === 'string' ? body.type : 'about:blank'
    this.title = typeof body.title === 'string' ? body.title : 'Request failed'
    this.requestId = typeof body.requestId === 'string' ? body.requestId : undefined
    this.body = body
  }
}

export interface ApiFetchOptions extends RequestInit {
  /** Injected in tests; defaults to the global fetch. */
  fetchImpl?: typeof fetch
}

/**
 * Calls the API and returns the parsed JSON body (undefined for 204). Failures throw an
 * {@link ApiProblem}; problem+json bodies are passed through.
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { fetchImpl = fetch, headers, ...init } = options
  const requestHeaders = new Headers(headers)
  if (!requestHeaders.has('Accept')) {
    requestHeaders.set('Accept', 'application/json')
  }
  const response = await fetchImpl(`${API_BASE}${path}`, {
    ...init,
    headers: requestHeaders,
    credentials: 'same-origin',
  })

  if (response.ok) {
    if (response.status === 204) {
      return undefined as T
    }
    return (await response.json()) as T
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes(PROBLEM_CONTENT_TYPE) || contentType.includes('application/json')) {
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>
    throw new ApiProblem(response.status, body)
  }
  throw new ApiProblem(response.status, {})
}
